import { useState, useEffect, useMemo } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { SchedulingSkillConfig } from "@/types/scheduling.types";

interface SchedulingSkillModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialValues: SchedulingSkillConfig | null;
  onSave: (config: SchedulingSkillConfig) => Promise<boolean>;
}

const schedulingSchema = z
  .object({
    booking_type: z.string().min(1, "Booking type is required"),
    scheduling_link: z
      .string()
      .url("Must be a valid URL")
      .optional()
      .or(z.string().length(0)),
    instructions: z.string().optional(),
  })
  .refine(
    (data) => {
      // If booking type is Calendly, scheduling_link is required
      if (
        data.booking_type === "Calendly" &&
        (!data.scheduling_link || data.scheduling_link.trim() === "")
      ) {
        return false;
      }
      return true;
    },
    {
      message: "Calendly URL is required when using Calendly booking type",
      path: ["scheduling_link"],
    },
  )
  .transform((data) => {
    // Clear scheduling_link when booking type is Manual
    if (data.booking_type === "Manual") {
      return {
        ...data,
        scheduling_link: "",
      };
    }
    return data;
  });

type FormValues = Omit<SchedulingSkillConfig, "enabled">;

const SchedulingSkillModal = ({
  isOpen,
  onClose,
  initialValues,
  onSave,
}: SchedulingSkillModalProps) => {
  const [isSaving, setIsSaving] = useState(false);

  // Memoize default values to avoid recreation on every render
  const defaultValues: FormValues = useMemo(
    () => ({
      booking_type: initialValues?.booking_type || "Calendly",
      scheduling_link: initialValues?.scheduling_link || "",
      instructions: initialValues?.instructions || "",
    }),
    [initialValues],
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(schedulingSchema),
    defaultValues,
  });

  // Update form when initialValues change
  useEffect(() => {
    if (initialValues) {
      form.reset({
        booking_type: initialValues.booking_type || "Calendly",
        scheduling_link: initialValues.scheduling_link || "",
        instructions: initialValues.instructions || "",
      });
    } else {
      form.reset(defaultValues);
    }
  }, [initialValues, form]);

  const onSubmit = async (data: FormValues) => {
    setIsSaving(true);
    try {
      // When saving from modal, always enable the skill
      const configToSave: SchedulingSkillConfig = {
        ...data,
        enabled: true,
      };

      const success = await onSave(configToSave);
      if (success) {
        toast.success("Scheduling settings saved and enabled successfully");
        onClose();
      } else {
        toast.error("Failed to save scheduling settings");
      }
    } catch (error) {
      console.error("Error saving scheduling configuration:", error);
      toast.error("An error occurred while saving configuration");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      modal={true}
    >
      <DialogPortal>
        <DialogOverlay className="bg-black/40" />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-[50%] top-[50%] z-50 w-[calc(100vw-48px)] max-w-[550px] translate-x-[-50%] translate-y-[-50%] rounded-xl border border-gray-200 bg-white p-6 shadow-xl duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
          )}
        >
          <DialogHeader>
            <DialogTitle>Configure Scheduling Settings</DialogTitle>
            <DialogDescription>
              Connect your calendar service to let your agent schedule
              appointments
            </DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-6 mt-4"
            >
              <FormField
                control={form.control}
                name="booking_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Booking Type</FormLabel>
                    <FormDescription>
                      Choose how you want the agent to handle scheduling
                      requests
                    </FormDescription>
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                      disabled={isSaving}
                    >
                      <FormControl>
                        <SelectTrigger className="bg-white">
                          <SelectValue placeholder="Select booking type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="Manual">Manual</SelectItem>
                        <SelectItem value="Calendly">Calendly</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Manual booking explainer */}
              {form.watch("booking_type") === "Manual" && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <div className="text-blue-600 mt-0.5">
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-blue-900 mb-1">Manual Scheduling</h4>
                      <p className="text-sm text-blue-700">
                        When using manual scheduling, your agent will ask users for their availability preferences and include this information in the email notification you receive. You can then coordinate the meeting directly with the user.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Scheduling link field - only show for Calendly */}
              {form.watch("booking_type") === "Calendly" && (
                <FormField
                  control={form.control}
                  name="scheduling_link"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Scheduling Link (Required for Calendly)
                      </FormLabel>
                      <FormDescription>
                        The agent will display this calendar directly within the chat conversation, with visitor email and name automatically pre-filled.
                        <span className="block mt-1 text-amber-600 font-medium">
                          ⚠️ Calendly URL is required when using Calendly booking type
                        </span>
                      </FormDescription>
                      <FormControl>
                        <Input
                          placeholder="https://calendly.com/yourusername"
                          {...field}
                          className="bg-white"
                          disabled={isSaving}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="instructions"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Instructions (Optional)</FormLabel>
                    <FormDescription>
                      Provide any specific instructions for how the agent should
                      handle scheduling requests
                    </FormDescription>
                    <FormControl>
                      <Textarea
                        placeholder="e.g., Ask about the preferred time zone, collect meeting agenda items, etc."
                        className="min-h-[80px] resize-y bg-white"
                        disabled={isSaving}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isSaving}>
                  {isSaving ? "Saving..." : "Save Settings"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
};

export default SchedulingSkillModal;
